import React, { useState, useEffect } from 'react';
import { useAuth } from '../lib/AuthContext';
import { supabase } from '../lib/supabase';
import { useTranslation } from '../lib/LanguageContext';
import { meetsPasswordPolicy } from '../lib/passwordPolicy';

export function ResetPasswordForm() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { updatePassword } = useAuth();
  const { t } = useTranslation();

  // Check if we have a hash fragment in the URL (from Supabase auth redirect)
  useEffect(() => {
    const handleHashChange = async () => {
      // Get the hash fragment from the URL
      const hash = window.location.hash.substring(1);
      
      if (hash) {
        try {
          // Parse the hash fragment
          const params = new URLSearchParams(hash);
          const accessToken = params.get('access_token');
          const refreshToken = params.get('refresh_token');
          const type = params.get('type');
          
          // If this is a recovery flow and we have tokens
          if (type === 'recovery' && accessToken) {
            // Set the session in Supabase
            const { error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken || '',
            });
            
            if (error) {
              console.error('Error setting session:', error);
              setError(t('invalidResetLink'));
            }

            // Clear the hash fragment from the URL to avoid issues on refresh
            window.history.replaceState(null, '', window.location.pathname);
          }
        } catch (err) {
          console.error('Error processing auth redirect:', err);
          setError(t('errorProcessingRequest'));
        }
      }
    };
    
    handleHashChange();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setLoading(true);

    if (password !== confirmPassword) {
      setError(t('newPasswordsDoNotMatch'));
      setLoading(false);
      return;
    }

    if (!meetsPasswordPolicy(password)) {
      setError(t('passwordRequirements'));
      setLoading(false);
      return;
    }

    try {
      await updatePassword(password);
      setMessage(t('passwordUpdatedSuccess'));
      setPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-md">
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-8">
          {t('resetYourPassword')}
        </h2>

        {message ? (
          <div className="space-y-6">
            <div className="bg-green-50 p-4 rounded-md">
              <p className="text-green-800">{message}</p>
            </div>
            <a
              href="/"
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              {t('returnToSignIn')}
            </a>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                {t('newPasswordLabel')}
              </label>
              <input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
              />
            </div>

            <div>
              <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700">
                {t('confirmNewPasswordLabel')}
              </label>
              <input
                id="confirm-password"
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
              />
            </div>

            {error && (
              <div className="text-red-500 text-sm">{error}</div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {loading ? t('processingButton') : t('resetPasswordButton')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}